import { useState } from 'react';
import { Card, CardContent } from '@ui/card.tsx';
import { ApiTokenDataTableSkeleton } from '@admin/components/datatable/api-token-data-table-skeleton.tsx';
import { TypologiesTable } from '@admin/components/typologies/typologies-table.tsx';
import { UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CreateTypologyDialog } from '@admin/components/typologies/create-typology-dialog.tsx';
import { UpdateTypologyDialog } from '@admin/components/typologies/update-typology-dialog.tsx';
import { useTypology } from '@admin/hooks/use-typology.ts';
import { useApiAdmintypologiesIdDelete } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import type { TypologyTypologyReadModel } from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type Typology = TypologyTypologyReadModel;

export const Typologies = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { typologies, isTypologiesLoading, isTypologiesSuccess, refetchTypologies } = useTypology();
  const user = useUser();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const canManageTypologies =
    activeWorkspace?.roles?.includes('ROLE_ADMIN') ||
    activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [typologyToEdit, setTypologyToEdit] = useState<Typology | null>(null);

  const deleteMutation = useApiAdmintypologiesIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.typology.delete.success'));
        void refetchTypologies();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/typologies')) {
          showOrvalErrorToast(error, t('admin.typology.delete.error'));
        } else {
          toast.error(t('admin.typology.delete.error'));
        }
      },
      networkMode: 'always'
    }
  });

  const handleTypologyCreated = async (): Promise<void> => {
    await refetchTypologies();
  };

  const handleEdit = (typology: Typology) => {
    setTypologyToEdit(typology);
    setEditModalOpen(true);
  };

  const handleDelete = (typology: Typology) => {
    if (typology.isDefault) {
      toast.error(t('admin.typology.delete.default.error'));
      return;
    }

    toast(t('admin.typology.delete.confirm', { name: typology.name }), {
      action: {
        label: t('label.confirm'),
        onClick: () => deleteMutation.mutate({ id: String(typology.id) })
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  if (isTypologiesLoading) {
    return (
      <Card className="p-10">
        <CardContent className="pt-6">
          <ApiTokenDataTableSkeleton columnCount={3} rowCount={4} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="pt-6 space-y-2.5">
          {isTypologiesSuccess && typologies?.data.length === 0 ? (
            <div className="flex flex-col items-center m-auto w-1/2 text-center">
              <UserCog size={50} className="mb-5 stroke-primary" />

              <h2 className="text-4xl font-bold">{t('admin.typology.creation.title')}</h2>
              <p className="py-5 text-muted-foreground">
                {t('admin.typology.creation.description')}
              </p>

              {canManageTypologies && (
                <CreateTypologyDialog onSubmitSuccess={handleTypologyCreated} />
              )}
            </div>
          ) : (
            <>
              <div className="w-2/3 space-y-2.5 pb-7">
                <h2 className="text-4xl font-bold">{t('admin.typologies.title')}</h2>
                <p className="text-muted-foreground">{t('admin.typologies.description')}</p>
              </div>

              <TypologiesTable
                data={typologies?.data ?? []}
                onCreateSuccess={handleTypologyCreated}
                onEdit={canManageTypologies ? handleEdit : undefined}
                onDelete={canManageTypologies ? handleDelete : undefined}
                canCreate={canManageTypologies}
                createdAt
              />
            </>
          )}
        </CardContent>
      </Card>

      <UpdateTypologyDialog
        typology={typologyToEdit}
        isOpen={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSuccess={() => void refetchTypologies()}
      />
    </>
  );
};
